import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight, Swords, Timer, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '@/components/common/Button';
import { PosizioneBoard } from '@/components/myBoards/PosizioneBoard';
import type { BoardDettaglio } from '@/hooks/useMyBoards';
import { useHaptics } from '@/hooks/useHaptics';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { useNomeGiocatore } from '@/hooks/useNomeGiocatore';
import { cn } from '@/lib/cn';
import { formatNumero } from '@/lib/fmt';
import type { ErroreBoard } from '@/lib/myBoards/errori';
import { notazioneCandidata } from '@/lib/myBoards/mosse';
import { sfidaDaBoardAcceso } from '@/lib/backgammon/myBoardsAcceso';
import { formaCubo, latoInVantaggio, minutiSecondiDaMs, sfidabile } from '@/lib/myBoards/riga';
import type { AnalysisClosedReason, BoardSide, BoardView, OriginPlayer } from '@/types/myBoards';

const lato = (s: BoardSide) => `myBoards.side.${s}`;

function Scheda({ titolo, children }: { titolo: string; children: ReactNode }) {
  return (
    <section className="card-edge rounded-2xl bg-bg-secondary p-4">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-fg-muted">{titolo}</h3>
      {children}
    </section>
  );
}

function Riga({ etichetta, children }: { etichetta: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-sm">
      <dt className="shrink-0 text-fg-muted">{etichetta}</dt>
      <dd className="min-w-0 break-words text-right text-fg">{children}</dd>
    </div>
  );
}

/**
 * Una tessera dell'aritmetica (T5): il numero in grande e cosa dice sotto. A 320 px la tessera e' di ~67 px utili: il numero
 * non va a capo (`129–137` su due righe e' un numero spezzato) e scende a 15 px; da 360 px torna a 18 (misurato nel banco).
 */
function Tessera({ valore, etichetta }: { valore: string; etichetta: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-bg-tertiary p-2 min-[360px]:p-3">
      <div className="whitespace-nowrap text-[15px] font-bold text-fg min-[360px]:text-lg">{valore}</div>
      <div className="text-xs text-fg-muted">{etichetta}</div>
    </div>
  );
}

function Pastiglia({ children, evidenza = false }: { children: ReactNode; evidenza?: boolean }) {
  return (
    <span
      className={cn(
        'rounded-full px-3 py-1.5 text-xs font-bold',
        evidenza ? 'bg-brand/15 text-brand' : 'bg-bg-tertiary text-fg',
      )}
    >
      {children}
    </span>
  );
}

/** Un'azione in forma di testo dentro una riga («replay qui ›»): la zona di tocco e' di 44 px anche se il testo e' piccolo. */
function Collegamento({ testid, onClick, children }: { testid: string; onClick: () => void; children: ReactNode }) {
  const haptics = useHaptics();
  return (
    <Button
      variant="link"
      data-testid={testid}
      onClick={() => {
        haptics.selection();
        onClick();
      }}
      className="min-h-11 min-w-11 justify-end gap-0.5 text-sm font-bold text-brand"
    >
      {children}
      <ChevronRight aria-hidden className="h-4 w-4" />
    </Button>
  );
}

const RAGIONE_CHIUSA: Record<AnalysisClosedReason, string> = {
  source_live: 'myBoards.analysis.closed.sourceLive',
  position_live: 'myBoards.analysis.closed.positionLive',
  terminal: 'myBoards.analysis.closed.terminal',
};

/**
 * La scheda di un board (T5 partita finita, T5b partita in corso): il tabellone grande, le tre pastiglie (turno e tiro, cubo,
 * punteggio), la partita d'origine (ADR-0191 §4), la posizione (aritmetica, §5 i), la stima del bot (§5 ii, chiusa con la sua
 * ragione finche' la partita e' viva) e «Sfida un amico da qui».
 *
 * T0: i dati sono finti (`useBoardDettaglio`). Nessun campo e' inventato qui: se manca, e' in `types/myBoards.ts`.
 * ⛔ Nessuna etichetta «Crawford»: nella partita da un board il cubo e' spento (L-2749 (a)), e sulla scheda il cubo e' solo
 * un dato per l'analisi.
 */
export function BoardScheda({
  board,
  dettaglio,
  mioId,
  onIndietro,
  onSfida,
  onApriReplay,
  onGuarda,
  onTogli,
}: {
  board: BoardView;
  dettaglio: BoardDettaglio;
  mioId: string | null;
  onIndietro: () => void;
  onSfida: () => void;
  onApriReplay: (board: BoardView) => void;
  onGuarda: (board: BoardView) => void;
  /** Toglie il board dalla lista: `null` se e' tolto (chi apre la scheda torna alla lista), altrimenti l'errore da dire. */
  onTogli: () => Promise<ErroreBoard | null>;
}) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const lingua = useLinguaFormato();
  const nome = useNomeGiocatore(mioId);
  const { position } = board;
  // L'origine e' quella di `GET /positions/:id` (la partita com'e' ADESSO) appena c'e', altrimenti quella che la lista aveva.
  const { stats, community, analysis, origin } = dettaglio;
  const [confermaTogli, setConfermaTogli] = useState(false);
  const [togliendo, setTogliendo] = useState(false);
  const [erroreTogli, setErroreTogli] = useState<ErroreBoard | null>(null);

  const sideTxt = (s: BoardSide) => t(lato(s));
  const roll = position.roll ? `${position.roll[0]}-${position.roll[1]}` : '';

  const turno =
    position.phase === 'gameOver'
      ? t('myBoards.detail.turn.gameOver')
      : t(`myBoards.detail.turn.${position.phase}`, { side: sideTxt(position.turn), roll });

  const cubo = formaCubo(position);
  const cuboTxt =
    cubo.kind === 'unknown'
      ? t('myBoards.detail.cube.unknown')
      : cubo.kind === 'centered'
        ? t('myBoards.detail.cube.centered', { value: cubo.value })
        : t('myBoards.detail.cube.owned', { value: cubo.value, side: sideTxt(cubo.owner) });

  const vantaggio = latoInVantaggio(stats.pip);
  const diffPip = Math.abs(stats.pip.W - stats.pip.B);
  const orologi =
    board.clocksAtCapture && !origin.timeControl.unlimited
      ? `${minutiSecondiDaMs(board.clocksAtCapture.W)} / ${minutiSecondiDaMs(board.clocksAtCapture.B)}`
      : null;

  const esito = (() => {
    const r = origin.result;
    if (origin.status === 'live' || r === null) return null;
    if (r.status === 'voided') return t('myBoards.detail.origin.voided');
    const vincitore = r.winner ? nome(origin.players[r.winner]) : null;
    if (r.status === 'abandoned') {
      return vincitore
        ? t('myBoards.detail.origin.abandonedWinner', { name: vincitore })
        : t('myBoards.detail.origin.abandoned');
    }
    return vincitore
      ? t('myBoards.detail.origin.winner', { name: vincitore, scoreW: r.score.W, scoreB: r.score.B })
      : t('myBoards.detail.origin.noWinner');
  })();

  const pct = (p: number) => formatNumero(p, lingua, { style: 'percent', maximumFractionDigits: 0 });
  // L'Elo e' quello a inizio partita, quando si sa: `null` ⇒ solo il nome, mai «Elo null».
  const giocatore = (p: OriginPlayer) =>
    p.elo === null
      ? t('myBoards.detail.origin.playerNoElo', { name: nome(p) })
      : t('myBoards.detail.origin.player', { name: nome(p), elo: p.elo });

  return (
    // `[&>*]:shrink-0`: i figli di una colonna che scorre non si schiacciano (un SVG senza altezza propria, il tabellone, si
    // ridurrebbe a zero invece di far scorrere la scheda: misurato nel banco a 390×844).
    <div data-testid="board-scheda" className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-4 [&>*]:shrink-0">
      <div className="flex items-center gap-2">
        <Button
          variant="icona"
          tono="pieno"
          size={40}
          data-testid="board-indietro"
          aria-label={t('myBoards.detail.back')}
          onClick={() => {
            haptics.selection();
            onIndietro();
          }}
          className="items-center justify-center"
        >
          <ArrowLeft aria-hidden className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-bold text-fg">{t('myBoards.detail.title')}</h2>
      </div>

      <PosizioneBoard position={position} ariaLabel={t('myBoards.board.aria', { move: origin.ply })} />

      <div className="flex flex-wrap justify-center gap-2" data-testid="board-pastiglie">
        <Pastiglia evidenza>{turno}</Pastiglia>
        <Pastiglia>{cuboTxt}</Pastiglia>
        <Pastiglia>
          {t('myBoards.detail.score', {
            scoreW: position.match.scoreW,
            scoreB: position.match.scoreB,
            length: position.match.length,
          })}
        </Pastiglia>
      </div>

      <Scheda titolo={t('myBoards.detail.origin.title')}>
        <dl>
          <Riga etichetta={sideTxt('W')}>
            {giocatore(origin.players.W)}
          </Riga>
          <Riga etichetta={sideTxt('B')}>
            {giocatore(origin.players.B)}
          </Riga>
          {origin.tournamentMatchId !== null && (
            <Riga etichetta={t('myBoards.detail.origin.tournament')}>{t('myBoards.detail.origin.tournamentValue')}</Riga>
          )}
          <Riga etichetta={t('myBoards.detail.origin.moment')}>
            <span className="inline-flex flex-wrap items-center justify-end gap-x-1.5">
              <span>{t('myBoards.detail.origin.momentValue', { game: position.match.gameNumber, ply: origin.ply })}</span>
              {orologi && (
                <span className="inline-flex items-center gap-1 text-fg-muted">
                  <Timer aria-hidden className="h-3.5 w-3.5" />
                  {orologi}
                </span>
              )}
            </span>
          </Riga>
          <Riga etichetta={t('myBoards.detail.origin.outcome')}>
            {origin.status === 'live' ? (
              <span className="flex flex-col items-end">
                <span>{t('myBoards.detail.origin.live')}</span>
                <Collegamento testid="board-guarda" onClick={() => onGuarda(board)}>
                  {t('myBoards.detail.origin.watch')}
                </Collegamento>
              </span>
            ) : (
              <span className="flex flex-col items-end">
                {esito && <span>{esito}</span>}
                <Collegamento testid="board-replay" onClick={() => onApriReplay(board)}>
                  {t('myBoards.detail.origin.replayHere')}
                </Collegamento>
              </span>
            )}
          </Riga>
        </dl>
      </Scheda>

      <Scheda titolo={t('myBoards.detail.position.title')}>
        <div className="grid grid-cols-3 gap-2" data-testid="board-aritmetica">
          <Tessera
            valore={`${stats.pip.W}–${stats.pip.B}`}
            etichetta={
              vantaggio
                ? t('myBoards.detail.position.pipLead', { side: sideTxt(vantaggio), diff: diffPip })
                : t('myBoards.detail.position.pipEven')
            }
          />
          <Tessera valore={`${stats.blots.W} – ${stats.blots.B}`} etichetta={t('myBoards.detail.position.blots')} />
          <Tessera valore={`${stats.homePoints.W} – ${stats.homePoints.B}`} etichetta={t('myBoards.detail.position.homePoints')} />
        </div>
      </Scheda>

      <Scheda titolo={t('myBoards.analysis.title')}>
        <p className="mb-2 text-xs text-fg-muted">{t('myBoards.analysis.caption')}</p>
        {analysis.kind === 'loading' ? (
          <p data-testid="board-stima-carico" className="text-sm text-fg-muted">
            {t('myBoards.analysis.loading')}
          </p>
        ) : analysis.kind === 'unavailable' ? (
          // «Non disponibile adesso» (503, rete): la frase e un «Riprova» a mano, nessun ciclo.
          <div data-testid="board-stima-non-disponibile" className="flex flex-col items-start gap-2">
            <p role="alert" className="text-sm text-fg-muted">
              {t('myBoards.error.analysisBusy')}
            </p>
            <Button variant="neutro" size="sm" data-testid="board-stima-riprova" onClick={dettaglio.riprovaAnalisi}>
              {t('myBoards.retry')}
            </Button>
          </div>
        ) : analysis.kind === 'closed' ? (
          <p data-testid="board-stima-chiusa" data-reason={analysis.reason} className="text-sm text-fg-muted">
            {t(RAGIONE_CHIUSA[analysis.reason])}
          </p>
        ) : (
          <dl data-testid="board-stima-aperta">
            <Riga etichetta={t('myBoards.analysis.wins', { side: sideTxt(position.turn) })}>
              {pct(analysis.analysis.winProb[position.turn])}
            </Riga>
            <Riga etichetta={t('myBoards.analysis.shots', { side: sideTxt(analysis.analysis.shots.player) })}>
              {t('myBoards.analysis.shotsValue', { count: analysis.analysis.shots.count })}
            </Riga>
            {analysis.analysis.candidates && position.roll && (
              <Riga etichetta={t('myBoards.analysis.withRoll', { roll })}>
                <ol className="flex flex-col items-end gap-0.5">
                  {analysis.analysis.candidates.map((c, i) => (
                    <li key={i}>{notazioneCandidata(c.moves, position)}</li>
                  ))}
                </ol>
              </Riga>
            )}
          </dl>
        )}
      </Scheda>

      {dettaglio.stato === 'caricamento' && (
        <p data-testid="board-carico" className="px-1 text-sm text-fg-muted">
          {t('myBoards.detail.loading')}
        </p>
      )}
      {dettaglio.stato === 'errore' && (
        <div data-testid="board-errore" className="flex flex-col items-start gap-2 px-1">
          <p role="alert" className="text-sm text-danger">
            {t('myBoards.detail.loadError')}
          </p>
          <Button variant="neutro" size="sm" data-testid="board-riprova" onClick={dettaglio.riprova}>
            {t('myBoards.retry')}
          </Button>
        </div>
      )}
      {dettaglio.stato === 'non-trovato' && (
        <p data-testid="board-non-trovato" role="alert" className="px-1 text-sm text-fg-muted">
          {t('myBoards.error.notFound')}
        </p>
      )}

      {community && (
        <p data-testid="board-comunita" className="px-1 text-xs text-fg-muted">
          {t('myBoards.detail.community', {
            saves: community.saves,
            plays: community.plays,
            white: community.whiteWins,
            black: community.blackWins,
          })}
        </p>
      )}

      {/* La regola e' lo scenario del SERVER (`null` = non giocabile): finche' la risposta non c'e' non si dice ne' «Sfida» ne' «non si gioca».
          🔴 E la sfida e' il T2: a `sfidaDaBoardAcceso()` spento non c'e' ne' il bottone ne' la nota (la nota e' solo per «non giocabile»). */}
      {dettaglio.stato === 'pronto' &&
        (sfidabile(position, dettaglio.scenario) ? (
          sfidaDaBoardAcceso() && (
          <Button
            fullWidth
            peso="bold"
            data-testid="board-sfida"
            onClick={() => {
              haptics.impact('light');
              onSfida();
            }}
            className="min-h-12 rounded-2xl text-base"
          >
            <Swords aria-hidden className="h-5 w-5" />
            {t('myBoards.detail.challenge')}
          </Button>
          )
        ) : (
          <p data-testid="board-non-giocabile" className="px-1 text-sm text-fg-muted">
            {t('myBoards.detail.notPlayable')}
          </p>
        ))}

      {/* «Togli»: dalla propria lista, con una conferma — il board resta nel mondo (una sfida puo' puntarci), sparisce da qui. */}
      {confermaTogli ? (
        <div
          role="group"
          aria-label={t('myBoards.detail.remove.cta')}
          data-testid="board-togli-conferma"
          className="card-edge flex flex-col gap-3 rounded-2xl bg-bg-secondary p-4"
        >
          <p className="text-sm text-fg">{t('myBoards.detail.remove.confirm')}</p>
          {erroreTogli && (
            <p role="alert" data-testid="board-togli-errore" className="text-sm text-danger">
              {t(erroreTogli.chiave)}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              variant="neutro"
              data-testid="board-togli-no"
              disabled={togliendo}
              className="min-h-11 flex-1"
              onClick={() => {
                haptics.selection();
                setConfermaTogli(false);
                setErroreTogli(null);
              }}
            >
              {t('myBoards.detail.remove.no')}
            </Button>
            <Button
              variant="danger"
              data-testid="board-togli-si"
              disabled={togliendo}
              className="min-h-11 flex-1"
              onClick={() => {
                haptics.impact('light');
                setTogliendo(true);
                setErroreTogli(null);
                // Tolto ⇒ chi apre la scheda ci riporta alla lista e questo componente sparisce: nessuno stato da aggiornare.
                void onTogli().then((e) => {
                  if (e === null) return;
                  setErroreTogli(e);
                  setTogliendo(false);
                });
              }}
            >
              {togliendo ? t('myBoards.detail.remove.busy') : t('myBoards.detail.remove.yes')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="neutro"
          fullWidth
          data-testid="board-togli"
          className="min-h-11"
          onClick={() => {
            haptics.selection();
            setConfermaTogli(true);
          }}
        >
          <Trash2 aria-hidden className="h-4 w-4" />
          {t('myBoards.detail.remove.cta')}
        </Button>
      )}
    </div>
  );
}
